import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PendingItem } from '../../services/r5d2Api';
import { useR5D2Reversal } from './hooks/useR5D2Reversal';

const { Title, Text } = Typography;

// ============================================================
// Helpers
// ============================================================

function renderStatus(status: string): React.ReactNode {
  if (!status || status === '') return <Tag color="default">未送審</Tag>;
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="orange">{status}</Tag>;
}

// ============================================================
// Main Component
// ============================================================

const R5D2Reversal: React.FC = () => {
  const {
    loading,
    reversing,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchReverse,
  } = useR5D2Reversal();

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<PendingItem> = [
    {
      title: '繳庫單號',
      dataIndex: 'doc_no',
      key: 'doc_no',
      width: 100,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '供應商名稱',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      ellipsis: true,
    },
    {
      title: '供應商代碼',
      dataIndex: 'vendor_code',
      key: 'vendor_code',
      width: 110,
    },
    {
      title: '部門',
      dataIndex: 'dept_name',
      key: 'dept_name',
      width: 100,
    },
    {
      title: '繳庫日期',
      dataIndex: 'receive_date',
      key: 'receive_date',
      width: 110,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '-',
    },
    {
      title: '版本',
      dataIndex: 'version_count',
      key: 'version_count',
      width: 60,
      align: 'center',
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter form handlers
  // ============================================================
  const handleSearch = () => { fetchPending(); };

  const handleFilterChange = (field: 'docStart' | 'docEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  // Auto-fill docEnd when docStart is entered (BR-F05 equivalent)
  const handleDocStartBlur = () => {
    if (filter.docStart && !filter.docEnd) {
      const autoEnd = (filter.docStart + 'zzzzzzzz').substring(0, 8);
      setFilter({ ...filter, docEnd: autoEnd });
    }
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>繳庫單核准沖銷 (pwr5d2f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label="單號起始">
            <Input
              value={filter.docStart}
              onChange={(e) => handleFilterChange('docStart', e.target.value)}
              onBlur={handleDocStartBlur}
              placeholder="例：FD240001"
              style={{ width: 120 }}
              maxLength={8}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label="單號結束">
            <Input
              value={filter.docEnd}
              onChange={(e) => handleFilterChange('docEnd', e.target.value)}
              placeholder="例：FD240099"
              style={{ width: 120 }}
              maxLength={8}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆繳庫單？沖銷後狀態將設為 N（未核准），並建立版本快照，無法還原。`}
              onConfirm={batchReverse}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={reversing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey="doc_no"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 750 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
    </div>
  );
};

export default R5D2Reversal;
